import { FilterFn } from "@tanstack/react-table";

export interface FilterDefinition<TItem> {
  id: string;
  label: string;
  getValues: (items: TItem[]) => any[];
  formatValue?: (value: any) => string;
  sort?: (a: any, b: any) => number;
}

// builds filter options based on provided items and filter definitions
export const buildFilterOptions = <TItem>(items: TItem[], filterDefs: FilterDefinition<TItem>[]) => {
  if (!items) return [];

  return filterDefs.map((filterDef) => {
    const values = filterDef.getValues(items).filter(Boolean);

    const uniqueValues = Array.from(new Set(values.map((value) => String(value))));

    const formattedValues = uniqueValues.map((value) => ({
      value,
      label: filterDef.formatValue ? filterDef.formatValue(value) : String(value)
    }));

    if (filterDef.sort) {
      formattedValues.sort((a, b) => filterDef.sort!(a.value, b.value));
    }

    return {
      value: filterDef.id,
      label: filterDef.label,
      options: formattedValues
    };
  });
};

export type ColumnFilter<T> = { id: string; value: any[] };
export type FilterMap<T> = Record<string, (item: T, values: any[]) => boolean>;

// builds a filter map based on provided filter definitions
export const buildFilterMap = <T>(filterDefs: FilterDefinition<T>[]) => {
  const filterMap: FilterMap<T> = {};

  filterDefs.forEach((filterDef) => {
    filterMap[filterDef.id] = (item: T, values: any[]) => {
      const itemValue = filterDef.getValues([item]);
      const valuesToCheck = Array.isArray(itemValue) ? itemValue.flat() : [itemValue];

      return valuesToCheck.some((val) => values.includes(String(val)));
    };
  });

  return filterMap;
};

// filters items based on provided column filters and filter map
export const filterItems = <T>(items: T[], columnFilters: ColumnFilter<T>[], filterMap: FilterMap<T>) => {
  if (!columnFilters || columnFilters.length === 0) return items;

  return items.filter((item) => {
    return columnFilters.every((filter) => {
      const filterFn = filterMap[filter.id];
      if (!filterFn) return true;

      return filterFn(item, filter.value);
    });
  });
};

// handles column filtering for tanstack table
export const columnFilterFn: FilterFn<any> = (row, columnId, filterValue) => {
  if (!filterValue || filterValue.length === 0) return true;

  const rowValue = row.getValue(columnId);

  if (Array.isArray(rowValue)) {
    return rowValue.some((val) => filterValue.includes(String(val)));
  }

  const normalizedRowValue = rowValue !== undefined && rowValue !== null ? String(rowValue).toLowerCase() : "";

  return filterValue.some((value: string) => normalizedRowValue.includes(String(value).toLowerCase()));
};
